import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, ChevronDown, ChevronRight, Copy, RefreshCw } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { reportClientError } from '@/shared/lib/clientErrorReporter'
import { isStaleChunkError, recoverFromStaleBuild, staleBuildText } from '@/shared/lib/staleBuildDetector'

/**
 * Who is looking at the fallback when this boundary catches.
 *
 * `team` (default) is a signed-in studio member: they get "Go to Dashboard" and a
 * collapsible diagnostics block to copy into a bug report.
 *
 * `client` is a **stranger** — somebody who followed an invoice-pay, offer-accept,
 * contract-accept, questionnaire or gallery link, or who is standing in front of
 * the sign-in page. They have no dashboard to return to, no session to lean on,
 * and no way to act on a stack trace. They get an apology, a reload, and nothing
 * else (CLAUDE.md: never show `error.message`, stack traces or status codes in
 * user-facing UI).
 */
type Audience = 'team' | 'client'

interface Props {
  children: React.ReactNode
  audience?: Audience
}

interface State {
  hasError: boolean
  isStaleChunk?: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
}

class ErrorBoundaryClass extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, isStaleChunk: isStaleChunkError(error) }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Always log to console for devtools — even if user is non-technical,
    // this lets us ask "open DevTools and paste what you see" to debug.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error)
    // eslint-disable-next-line no-console
    console.error('Component stack:', errorInfo.componentStack)
    this.setState({ errorInfo })
    reportClientError({
      message: error.message,
      stack: error.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      errorBoundary: true,
      componentStack: errorInfo.componentStack ?? undefined,
    })
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isStaleChunk) {
        return <StaleBuildFallback />
      }
      if (this.props.audience === 'client') {
        return <ClientErrorFallback />
      }
      return (
        <ErrorFallback
          onReset={this.resetError}
          error={this.state.error}
          errorInfo={this.state.errorInfo}
        />
      )
    }

    return this.props.children
  }
}

interface ErrorFallbackProps {
  onReset: () => void
  error?: Error
  errorInfo?: React.ErrorInfo
}

function ErrorFallback({ onReset, error, errorInfo }: ErrorFallbackProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [showDetails, setShowDetails] = React.useState(false)
  const [copied, setCopied] = React.useState(false)

  const handleGoToDashboard = () => {
    onReset()
    navigate('/')
    window.location.reload()
  }

  const errorText = [
    `URL: ${typeof window !== 'undefined' ? window.location.href : '(unknown)'}`,
    `User-Agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : '(unknown)'}`,
    '',
    `Message: ${error?.message || '(no message)'}`,
    '',
    `Stack:`,
    error?.stack || '(no stack)',
    '',
    `Component stack:`,
    errorInfo?.componentStack || '(no component stack)',
  ].join('\n')

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(errorText)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard unavailable — ignore */
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="flex flex-col items-center justify-center gap-6 max-w-xl px-6 text-center">
        <div className="rounded-full bg-destructive/10 p-4">
          <AlertCircle className="h-8 w-8 text-destructive" />
        </div>

        <div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {t('errors.boundaryFallback.title', 'Something went wrong')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('errors.boundaryFallback.message', 'An unexpected error occurred. Please try again or return to the dashboard.')}
          </p>
          {/* Raw error.message removed from above-the-fold copy 2026-04-30:
              Safari's "Can't find variable: global" was surfacing verbatim and
              looked like alarming jargon to non-technical studio users. The
              technical details (incl. error.message) are still available in
              the collapsible "Show details" / Copy-report block below for
              the rare case where the user needs to send a report to support. */}
        </div>

        <div className="flex gap-3 w-full">
          <Button
            variant="outline"
            size="default"
            onClick={onReset}
            className="flex-1"
          >
            Try again
          </Button>
          <Button
            size="default"
            onClick={handleGoToDashboard}
            className="flex-1"
          >
            Go to Dashboard
          </Button>
        </div>

        {/* Diagnostics — collapsed by default. Gives non-technical users
            something to copy-paste when reporting the bug. */}
        <div className="w-full text-left">
          <button
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setShowDetails(s => !s)}
          >
            {showDetails ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            {showDetails ? 'Hide details' : 'Show details'}
          </button>
          {showDetails && (
            <div className="mt-2 rounded-md border bg-muted/40 p-3 space-y-2">
              <div className="flex justify-end">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleCopy}>
                  <Copy size={12} className="mr-1" />
                  {copied ? 'Copied' : 'Copy report'}
                </Button>
              </div>
              <pre className="whitespace-pre-wrap break-words text-[11px] font-mono leading-relaxed text-muted-foreground max-h-80 overflow-auto">
                {errorText}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * The fallback a **client** sees — invoice-pay, offer-accept, contract-accept,
 * questionnaire, gallery, booking, sign-in, and every marketing page.
 *
 * Deliberately not the team one. A client has no dashboard to be sent to, no
 * session to reset, and nothing useful to do with a component stack; before this
 * existed they got a blank white page and their next move was to phone the
 * studio. So: an apology, one thing to try, and who to ask. No `error.message`,
 * no stack, no status code, no "Copy report", no app chrome (CLAUDE.md's
 * "Errors to users" rule, and its non-technical-user rule).
 *
 * The error is still reported to telemetry and the console by
 * `componentDidCatch` above — the diagnosis simply does not go on screen.
 */
function ClientErrorFallback() {
  const { t } = useTranslation()

  return (
    <div className="flex items-center justify-center min-h-screen bg-background px-6">
      <div
        role="alert"
        className="flex flex-col items-center justify-center gap-6 max-w-md text-center"
      >
        <div className="rounded-full bg-destructive/10 p-4">
          <AlertCircle className="h-8 w-8 text-destructive" />
        </div>

        <div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {t('errors.publicBoundaryFallback.title', 'Something went wrong')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              'errors.publicBoundaryFallback.message',
              'We are sorry — this page could not be displayed. Please reload the page and try again. If it keeps happening, contact the studio for help.',
            )}
          </p>
        </div>

        <Button size="default" className="min-h-10" onClick={() => window.location.reload()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('errors.publicBoundaryFallback.reload', 'Reload page')}
        </Button>
      </div>
    </div>
  )
}

/**
 * The React-caught half of stale-build recovery (the DOM-injected half lives in
 * staleBuildDetector). Both go through {@link recoverFromStaleBuild} so there is
 * ONE policy: reload straight away, defer while an upload is running (IN-163),
 * and only fall back to a manual banner when a reload has just been tried and
 * did not help.
 *
 * Text goes through {@link staleBuildText} rather than the `useTranslation()`
 * hook, here as in the detector: the chunk that failed to load may well be the
 * one carrying the translations, and the singleton `t()` call with an English
 * default can't suspend or throw the way a hook bound to Suspense could.
 */
function StaleBuildFallback() {
  const [outcome, setOutcome] = React.useState<'reloading' | 'manual' | 'deferred' | 'leaving'>('reloading')

  React.useEffect(() => {
    const result = recoverFromStaleBuild()
    if (result !== 'reloading') setOutcome(result)
  }, [])

  // A reload is already in flight, or the page is being navigated away from
  // (IN-189) — showing anything would flash for one frame.
  if (outcome === 'reloading' || outcome === 'leaving') return null

  if (outcome === 'deferred') {
    // IN-163: uploads are running. No reload affordance on purpose — a
    // non-technical user pressing it would destroy their own in-progress
    // upload. The page reloads on its own once the queue goes idle.
    return (
      <div
        role="status"
        className="fixed inset-x-0 top-0 z-50 bg-slate-900 text-slate-50 px-5 pb-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center shadow-lg text-sm font-medium
                   [padding-top:max(0.75rem,env(safe-area-inset-top))]"
      >
        <RefreshCw className="h-4 w-4 shrink-0" />
        <span>
          {staleBuildText(
            'errors.staleBuild.deferredMessage',
            'A new version is available. It will load when your uploads finish.',
          )}
        </span>
      </div>
    )
  }

  return (
    <div
      className="fixed inset-x-0 top-0 z-50 bg-slate-900 text-slate-50 px-5 pb-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center shadow-lg text-sm font-medium
                 [padding-top:max(0.75rem,env(safe-area-inset-top))]"
    >
      <RefreshCw className="h-4 w-4 shrink-0" />
      <span>{staleBuildText('errors.staleBuild.message', 'A new version is available.')}</span>
      <Button
        size="sm"
        className="shrink-0 min-h-10 bg-blue-500 hover:bg-blue-600 text-white border-0"
        onClick={() => window.location.reload()}
      >
        {staleBuildText('errors.staleBuild.reload', 'Reload')}
      </Button>
    </div>
  )
}

export { ErrorBoundaryClass as ErrorBoundary }
